import { useEffect, useRef, useState } from 'react';
import BudgetPicker from './BudgetPicker';
import AmountInput from '../../components/AmountInput';
import DateInput from '../../components/DateInput';
import ImpactPicker from './ImpactPicker';
import LinkedStatements from './LinkedStatements';
import QuickLinkButton from './QuickLinkButton';
import { cashCents, formatCurrency } from '../../Helpers';
import { applyImpact } from '../../Impact';

interface Props {
    bankTransaction: BankTransaction | NewBankTransaction,
    budgets: Budget[],
    onChange: (params: UpdateBankTransactionParams) => void
    // Résout à false si le renommage est annulé
    onRename: (userLabel: string) => Promise<boolean>
    onDelete: () => void
    // Opérations liées : seulement pour une opération enregistrée
    accounts?: Account[]
    accountId?: number
    onLink?: (preselectedId?: number | null) => void
    // Envoyer l'opération dans un Tricount (opérations de banque seulement)
    onSendToTricount?: () => void
    onRemoveLink?: (link: StatementLink) => void
    onQuickLink?: (suggestion: LinkSuggestion) => void
    // Relevé d'un Tricount : l'impact cash de chaque ligne, sous le montant.
    showCash?: boolean
}

const BankTransactionsListItem = (props: Props) => {
    const { bankTransaction, budgets, onChange, onRename, onDelete, accounts, accountId, onLink, onSendToTricount, onRemoveLink, onQuickLink, showCash } = props
    const suggestion = 'link_suggestion' in bankTransaction ? bankTransaction.link_suggestion : null
    const rowRef = useRef<HTMLDivElement>(null)

    const isNewTransaction = !('id' in bankTransaction)
    const [label, setLabel] = useState(bankTransaction.label)
    useEffect(() => setLabel(bankTransaction.label), [bankTransaction.label])

    // Opération importée : son libellé banque est figé, on la renomme avec un libellé perso.
    const savedUserLabel = ('user_label' in bankTransaction && bankTransaction.user_label) || ''
    const userLabelInherited = 'user_label_inherited' in bankTransaction && bankTransaction.user_label_inherited
    const [userLabel, setUserLabel] = useState(savedUserLabel)
    useEffect(() => setUserLabel(savedUserLabel), [savedUserLabel])

    const saveUserLabel = () => {
        if(userLabel.trim() === savedUserLabel) {
            setUserLabel(savedUserLabel)
            return
        }
        onRename(userLabel).then(applied => {
            if(!applied) {
                setUserLabel(savedUserLabel)
            }
        })
    }

    const isToCategorize = !isNewTransaction && !bankTransaction.budget_id

    const paidBy = 'paid_by' in bankTransaction ? bankTransaction.paid_by : null
    const cash = cashCents(bankTransaction)
    const share = Math.round(applyImpact(bankTransaction.amount_cents, bankTransaction.impact))
    const cashTitle = paidBy
        ? (bankTransaction.amount_cents < 0
            ? `Reçu par ${paidBy} : votre part (${formatCurrency(-share)}) compte dans le budget, mais rien n'est entré dans votre poche. ${paidBy} vous doit ${formatCurrency(-share)}.`
            : `Payé par ${paidBy} : votre part (${formatCurrency(share)}) compte dans le budget, mais rien n'est sorti de votre poche. Vous devez ${formatCurrency(share)} à ${paidBy}.`)
        : (bankTransaction.amount_cents < 0 ? 'Reçu par vous : tout le montant est entré dans votre poche' : 'Payé par vous : tout le montant est sorti de votre poche')

    const links = !isNewTransaction && accounts && accountId !== undefined && onRemoveLink && <>
        <LinkedStatements bankTransaction={bankTransaction} accounts={accounts} accountId={accountId} onRemove={onRemoveLink}/>
        { suggestion && suggestion.obvious && onQuickLink && <QuickLinkButton
            bankTransaction={bankTransaction}
            suggestion={suggestion}
            accounts={accounts}
            budgets={budgets}
            onClick={() => onQuickLink(suggestion)}/> }
        { suggestion && !suggestion.obvious && onLink && <span
            className="link-suggestion"
            title="Une opération d'un autre compte a le même montant, à quelques jours près"
            onClick={() => onLink(suggestion.id)}>
            <i className="fa-solid fa-link"/> lien possible ?
        </span> }
    </>

    return <div
        className={`bank-transaction-row ${isToCategorize ? 'to-categorize' : ''}`}
        ref={rowRef}
            >
        <div className="date">
            <DateInput
                value={bankTransaction.at}
                onBlur={(date) => onChange({at: date})}
                disabled={bankTransaction.is_protected}/>
            </div>
        { bankTransaction.is_protected
            ? <div className="label">
                <input value={userLabel}
                    placeholder={bankTransaction.label}
                    title={savedUserLabel ? undefined : 'Cliquer pour renommer'}
                    onChange={(e) => setUserLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                    onBlur={saveUserLabel}/>
                { 'adjustment' in bankTransaction && bankTransaction.adjustment && <span
                    className="adjustment-badge"
                    title="Calculée à chaque synchro pour que le relevé égale l'encours banque : frais que la banque ne transmet pas (frais carte étranger), ou opérations pas encore synchronisées">auto</span> }
                { savedUserLabel && userLabelInherited && <span
                    className="user-label-inherited"
                    title="Repris d'une opération au même libellé banque">↺ hérité</span> }
                { links }
                { savedUserLabel && <span className="bank-label">
                    <i className="fa-solid fa-building-columns"/>
                    <span className="bank-label-tooltip">
                        <small>Libellé banque</small>
                        <span>{bankTransaction.label}</span>
                    </span>
                </span> }
            </div>
            : <div className="label">
                <input value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onBlur={() => onChange({label})}/>
                { links }
            </div> }
        <div className={`amount ${bankTransaction.amount_cents < 0 ? 'credit' : ''}`}>
            <AmountInput
                value={bankTransaction.amount_cents}
                onChange={(amountCents) => onChange({amount_cents: amountCents})}
                disabled={bankTransaction.is_protected}/>
            { showCash && !isNewTransaction && <div className={`cash-line ${cash === 0 ? 'zero' : ''}`} title={cashTitle}>
                cash { formatCurrency(cash) }{ paidBy && <> · { paidBy }</> }
            </div> }
        </div>
        <div className="category">
            <BudgetPicker
                value={bankTransaction.budget_id}
                suggestedValue={bankTransaction.suggested_budget_id}
                onChange={(budget_id) => onChange({budget_id})}
                budgets={budgets}
                disabled={isNewTransaction}
                />
        </div>
        <div className="impact-ratio">
            <ImpactPicker
                value={bankTransaction.impact}
                onChange={(impact) => onChange({impact})}
                amountCents={bankTransaction.amount_cents}
                disabled={isNewTransaction}/>
        </div>
        <div className="is-protected">
            { !isNewTransaction && onLink && !paidBy && <i
                className="fa-solid fa-link link-button"
                title="Lier à une opération d'un autre compte"
                onClick={() => onLink()}/> }
            { !isNewTransaction && onSendToTricount && <i
                className="fa-solid fa-share-nodes link-button"
                title="Envoyer dans un Tricount"
                onClick={onSendToTricount}/> }
            { 'pending' in bankTransaction && bankTransaction.pending && <i className="fa-regular fa-clock" title="En attente de débit"/>}
            { bankTransaction.is_protected && !('pending' in bankTransaction && bankTransaction.pending) && <i className="fa-solid fa-lock"/>}
            { !bankTransaction.is_protected && <i
                className="fa-solid fa-trash delete-button"
                onClick={onDelete}/>}
        </div>
    </div>
}

export default BankTransactionsListItem